import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import Modal from '@mui/material/Modal';
import {
  Box,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { showErrorToaster } from '../../Common/ComponentToast/ComponentSuccessToasts';
import { RootState } from '../../redux/rootState';
import { getEquipmentReport } from '../JobScheduling/UnscheduledJobs/addJob/redux/jobScheduleSlice';
import { exportToExcelData } from '../../Common/PageHeader/helper';
import { Cancel } from '../../media/images/Cancel';
import { PrintIcon } from '../../assets/TableIcons';
import DataTable from '../../Common/DataTable';
import { EquipmentReportTableData } from './utils/TypeDashboard';
import { setLoader } from '../../redux/loaderSlice';
import { Entries } from '../../Common/ComponentSelectInput/types';
import { getAnalystMasterStart } from '../Job/ScreenBidWorkSheetJob/redux/bidWorksheetSlice';
import {
  stylesTabIndicator, selectedTabStyles, tabStyles, tabsStyles,
} from '../../Common/TabsStyles';

interface IForecastReportModal {
  open: boolean;
  handleClose: () => void;
}

export default function EquipmentAssignedModal({
  open, handleClose,
}: IForecastReportModal) {
  const Datatablestyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    bgcolor: 'background.paper',
    borderRadius: '6px',
    boxShadow: 24,
    p: '6px 24px 24px 24px',
    height: 'auto',
    minHeight: '450px',
    '@media (max-width: 768px)': {
      p: '6px 6px 15px',
      height: 'auto',
    },
  };

  const dispatch = useDispatch();
  const { analystList } = useSelector((state: RootState) => state.bidsReducer);
  const [selectedAnalyst, setSelectedAnalyst] = useState('0');
  const [activeTab, setActiveTab] = React.useState(1);

  const allAnalysts = useMemo(() => [{ Text: 'ALL', Value: '0' }, ...analystList], [analystList]);
  useEffect(() => {
    dispatch(getAnalystMasterStart());
  }, []);

  const getData = () => {
    if (selectedAnalyst) {
      dispatch(getEquipmentReport(selectedAnalyst === '0' ? '' : selectedAnalyst));
    }
  };

  useEffect(() => {
    getData();
  }, [selectedAnalyst]);

  const TABS = [
    { id: 1, label: 'Job' },
    { id: 2, label: 'Non-Job' },
  ];

  const handleChangeTab = (tabId: number) => {
    setActiveTab(tabId);
  };

  const { equipmentReportData, isLoading } = useSelector((state: RootState) => state.jobScheduleReducer);

  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading]);

  const [jobReports, nonJobReports] = useMemo(() => {
    const job = equipmentReportData?.Job?.map((item) => ({
      id: item?.Product,
      jobOrder: item?.JobOrder,
      assignedDate: item?.AssignedDate,
      jobDate_nosort: item?.JobDates,
      category: item?.Category,
      productId: item?.Product,
      description: item?.Description,
      serialId: item?.Serial,
      location: item?.Location,
      assignedTo: item?.AnalystName,
    }));
    const nonJob = equipmentReportData?.NonJob?.map((item) => ({
      id: item?.Product,
      assignedDate: item?.AssignedDate,
      category: item?.Category,
      productId: item?.Product,
      description: item?.Description,
      serialId: item?.Serial,
      location: item?.Location,
      assignedTo: item?.AnalystName,
    }));
    return [job, nonJob];
  }, [equipmentReportData]);

  const jobColumns = [
    { key: 'jobOrder', label: 'Job Order #' },
    { key: 'assignedDate', label: 'Assigned Date' },
    { key: 'jobDate_nosort', label: 'Job Date' },
    { key: 'category', label: 'Category' },
    { key: 'productId', label: 'Product #' },
    { key: 'description', label: 'Description' },
    { key: 'serialId', label: 'Serial #' },
    { key: 'location', label: 'Location' },
    { key: 'assignedTo', label: 'Assigned To' },
  ];
  const nonJobColumns = [
    { key: 'assignedDate', label: 'Assigned Date' },
    { key: 'category', label: 'Category' },
    { key: 'productId', label: 'Product #' },
    { key: 'description', label: 'Description' },
    { key: 'serialId', label: 'Serial #' },
    { key: 'location', label: 'Location' },
    { key: 'assignedTo', label: 'Assigned To' },
  ];

  const downloadExcel = () => {
    const analName = allAnalysts?.find((it) => String(it.Value) === String(selectedAnalyst));
    if (activeTab === 1 && jobReports?.length > 0) {
      const exportedData = jobReports?.map((items) => ({
        'Job Order #': items?.jobOrder || '',
        'Assigned Date': items?.assignedDate || '',
        'Job Date': items?.jobDate_nosort || '',
        Category: items?.category || '',
        'Product #': items?.productId || '',
        Description: items?.description || '',
        'Serial #': items?.serialId || '',
        Location: items?.location || '',
        'Assigned To': items?.assignedTo || '',
      }));
      exportToExcelData(
        {
          data: exportedData?.length > 0 ? exportedData : [],
          removeSn: true,
        },
        `Job Equipment Issued Report${analName?.Text ? ` - ${analName?.Text}` : ''}`,
      );
    } else if (activeTab === 2 && nonJobReports?.length > 0) {
      const exportedData = nonJobReports?.map((items) => ({
        'Assigned Date': items?.assignedDate || '',
        Category: items?.category || '',
        'Product #': items?.productId || '',
        Description: items?.description || '',
        'Serial #': items?.serialId || '',
        Location: items?.location || '',
        'Assigned To': items?.assignedTo || '',
      }));
      exportToExcelData(
        {
          data: exportedData?.length > 0 ? exportedData : [],
          removeSn: true,
        },
        `Non-Job Equipment Issued Report${analName?.Text ? ` - ${analName?.Text}` : ''}`,
      );
    } else {
      showErrorToaster('No records to export');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={Datatablestyle}>
        <div className="customerInfo_container flex-row">
          <div className="customerInfo">
            Equipment Issued Report
          </div>
          <div className="cancel-icon" onClick={() => handleClose()}>
            <Cancel />
          </div>

        </div>
        <div className="customModalBtnContainer">
          <Tabs
            variant="scrollable"
            value={activeTab - 1}
            onChange={(_, newValue) => handleChangeTab(TABS[newValue].id)}
            style={tabsStyles}
            TabIndicatorProps={stylesTabIndicator}
          >
            {TABS.map((tab) => (
              <Tab
                key={tab.id}
                label={tab.label}
                style={activeTab === tab.id ? selectedTabStyles : tabStyles}
                className="tab_styles"
              />
            ))}
          </Tabs>
          <div style={{
            display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center',
          }}
          >
            <div className="forecastButtonContainer">
              <Grid item xs={12} sm={12} md={3} lg={3}>
                <label style={{ fontSize: '14px', fontWeight: '500', marginRight: '5px' }}>
                  Analyst
                </label>
                <Select
                  size="small"
                  value={String(selectedAnalyst)}
                  style={{ width: '250px' }}
                  onChange={(event: SelectChangeEvent<string | string>) => setSelectedAnalyst(event.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: '220px', // Adjust the max height value as needed
                        maxWidth: '250px',
                      },
                    },
                  }}
                >
                  {allAnalysts.map((item: Entries) => (
                    <MenuItem className="menu-item-enter" value={item.Value} key={item.Value}>
                      {item.Text}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </div>
            <div className="pointCursor" title="Print Report Log" onClick={downloadExcel}>
              <PrintIcon />
            </div>
          </div>
        </div>
        <DataTable<EquipmentReportTableData>
          data={activeTab === 1 ? jobReports : nonJobReports}
          columns={activeTab === 1 ? jobColumns : nonJobColumns}
          handleData={() => {}}
          totalRecords={10}
        />
      </Box>
    </Modal>
  );
}
